import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Utensils, Search, Loader2, ChevronRight, Shuffle, Flame } from "lucide-react";

const STATUS = { draft: "bg-yellow-50 text-yellow-700 border-yellow-200", reviewed: "bg-blue-50 text-blue-700 border-blue-200", approved: "bg-green-50 text-green-700 border-green-200", sent: "bg-[#FF7A00]/10 text-[#FF7A00] border-[#FF7A00]/20" };
const FILTERS = ["all", "draft", "reviewed", "approved", "sent"];
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

const PlanCard = ({ p, onOpen }) => (
  <Card onClick={onOpen} className="group rounded-xl border-stone-200/70 p-4 cursor-pointer hover:border-[#FF7A00]/40 hover:shadow-sm transition-[border-color,box-shadow]" data-testid={`meal-plan-card-${p.plan_id}`}>
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-[#FFF7F0] flex items-center justify-center shrink-0"><Utensils className="w-5 h-5 text-[#FF7A00]" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-stone-900 truncate">{p.patient_name || "Unknown patient"}</p>
          <Badge variant="outline" className={`text-[10px] capitalize ${STATUS[p.status] || STATUS.draft}`}>{p.status || "draft"}</Badge>
          {p.version > 1 && <Badge variant="outline" className="text-[10px]">v{p.version}</Badge>}
          {p.pending_swaps > 0 && <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"><Shuffle className="w-3 h-3 mr-1" />{p.pending_swaps} swap request{p.pending_swaps > 1 ? "s" : ""}</Badge>}
        </div>
        <p className="text-xs text-stone-600 mt-1 flex items-center gap-1"><Flame className="w-3 h-3 text-[#FF7A00]" />{p.energy_target || 1800} kcal · {p.plan_data?.total_days || 14} days{p.plan_data?.medical_condition ? ` · ${p.plan_data.medical_condition}` : ""}</p>
        <p className="text-[11px] text-stone-400 mt-0.5">Created {fmt(p.created_at)}{p.sent_at ? ` · Sent ${fmt(p.sent_at)}` : ""}</p>
      </div>
      <Button size="sm" variant="ghost" className="text-xs text-[#FF7A00] shrink-0" data-testid={`open-meal-plan-${p.plan_id}`}>Open<ChevronRight className="w-3.5 h-3.5 ml-0.5 transition-transform group-hover:translate-x-0.5" /></Button>
    </div>
  </Card>
);

const MealPlansPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    const t = setTimeout(() => {
      const q = new URLSearchParams({ search, status: status === "all" ? "" : status });
      fetch(`${API}/staff/generated-meal-plans?${q}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(setData).catch(() => setData({ meal_plans: [], status_counts: {} }));
    }, 250);
    return () => clearTimeout(t);
  }, [search, status, token]);

  const counts = data?.status_counts || {};
  const all = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="meal-plans-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Meal Plans</h1>
        <p className="mt-1 text-base text-stone-500">{all} meal plans across all patients</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder="Search by patient name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-lg" data-testid="meal-plans-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map(f => (
            <button key={f} onClick={() => setStatus(f)} data-testid={`meal-plans-filter-${f}`} className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-colors ${status === f ? "bg-[#FF7A00] text-white border-[#FF7A00]" : "bg-white text-stone-600 border-stone-200 hover:border-[#FF7A00]/40"}`}>
              {f} {f === "all" ? `(${all})` : counts[f] ? `(${counts[f]})` : ""}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
      ) : data.meal_plans.length ? (
        <div className="grid gap-3 lg:grid-cols-2" data-testid="meal-plans-list">
          {data.meal_plans.map(p => <PlanCard key={p.plan_id} p={p} onOpen={() => navigate(`/admin/meal-plans/${p.plan_id}`)} />)}
        </div>
      ) : (
        <Card className="rounded-xl border-stone-200/60 p-12 text-center" data-testid="meal-plans-empty">
          <Utensils className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500">No meal plans found.</p>
        </Card>
      )}
    </div>
  );
};

export default MealPlansPage;
